import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Gem, Lock } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { Btn, Loading, SectionHead } from "@/components/public/Ui";
import {
  ConsentCheck,
  Field,
  FormBanner,
  Honeypot,
  SelectInput,
  TextArea,
  TextInput,
} from "@/components/public/FormKit";

const STEPS = ["Nominee Details", "Contribution", "Your Details", "Review & Submit"];

const EMPTY = {
  nominee_name: "",
  category: "",
  nominee_bio: "",
  nomination_reason: "",
  nominator_name: "",
  nominator_email: "",
  nominator_phone: "",
  consent: false,
  honeypot: "",
};

export default function EmeraldNominate() {
  const [config, setConfig] = useState(null);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState({ status: "idle", message: "" });
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/public/emerald/config").then((r) => setConfig(r.data)).catch(() => setConfig(false));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validateStep = (s) => {
    const e = {};
    if (s === 0) {
      if (form.nominee_name.trim().length < 2) e.nominee_name = "Please enter the nominee's name.";
      if (!form.category) e.category = "Please choose a category.";
    }
    if (s === 1) {
      if (form.nomination_reason.trim().length < 20)
        e.nomination_reason = "Please tell us why this nominee should be recognised (at least a sentence or two).";
    }
    if (s === 2) {
      if (form.nominator_name.trim().length < 2) e.nominator_name = "Please enter your name.";
      if (!/^\S+@\S+\.\S+$/.test(form.nominator_email))
        e.nominator_email = "Please enter a valid email address.";
    }
    if (s === 3 && !form.consent) e.consent = "Please acknowledge the privacy notice to continue.";
    return e;
  };

  const next = () => {
    const e = validateStep(step);
    setErrors(e);
    if (Object.keys(e).length === 0) setStep((s) => Math.min(s + 1, 3));
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const e = { ...validateStep(0), ...validateStep(1), ...validateStep(2), ...validateStep(3) };
    setErrors(e);
    if (Object.keys(e).length) {
      setBanner({ status: "error", message: "Please review the highlighted fields." });
      return;
    }
    setBanner({ status: "loading", message: "" });
    try {
      const { data } = await api.post("/public/emerald/nominations", form);
      setBanner({ status: "ok", message: data.message });
      setDone(true);
      setForm(EMPTY);
    } catch (err) {
      setBanner({ status: "error", message: apiError(err) });
    }
  };

  if (config === null) return <Loading label="Loading the nomination form" />;

  const closed = config && config.nominations_open === false;

  return (
    <div className="bg-bka-emBg" data-testid="emerald-nominate-page">
      <Seo
        title="Submit a Nomination | Emerald Honours"
        description="Nominate an individual or organisation making a lasting contribution to kidney health in The Bahamas."
        canonicalPath="/emerald-honours/nominate"
      />

      <section className="relative overflow-hidden border-b border-white/10">
        <div className="bka-grain absolute inset-0" aria-hidden="true" />
        <div className="bka-container relative py-14 lg:py-20">
          <Link
            to="/emerald-honours"
            data-testid="nominate-back-link"
            className="bka-overline text-bka-gold transition-colors hover:text-white"
          >
            ← Emerald Honours
          </Link>
          <h1 className="mt-5 max-w-2xl font-display text-4xl font-bold leading-[1.05] text-white sm:text-5xl">
            Nomination Form
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-white/75">
            Help us recognise outstanding individuals and organisations who are making a difference in
            kidney health across The Bahamas.
          </p>
        </div>
      </section>

      <div className="bka-container grid gap-10 py-14 lg:grid-cols-[1.35fr_0.65fr] lg:gap-14 lg:py-20">
        <div>
          {closed ? (
            <div
              data-testid="nominate-closed"
              className="rounded-3xl border border-bka-gold/30 bg-bka-gold/10 p-8 lg:p-12"
              role="status"
            >
              <Lock className="h-6 w-6 text-bka-gold" />
              <h2 className="mt-5 font-display text-2xl font-bold text-white">Nominations are closed</h2>
              <p className="mt-4 text-base text-white/75">{config.closed_message}</p>
              <div className="mt-8">
                <Btn to="/emerald-honours" variant="gold" testId="nominate-closed-back">
                  Back to Emerald Honours
                </Btn>
              </div>
            </div>
          ) : done ? (
            <div
              data-testid="nominate-success"
              className="rounded-3xl border border-bka-emerald/40 bg-bka-emCard p-8 lg:p-12"
              role="status"
            >
              <CheckCircle2 className="h-8 w-8 text-bka-gold" />
              <h2 className="mt-5 font-display text-2xl font-bold text-white">Nomination received</h2>
              <p className="mt-4 text-base leading-relaxed text-white/75">{banner.message}</p>
              <p className="mt-3 text-sm text-white/55">
                Your nomination is private. It is reviewed by the association and nothing is published
                unless it is explicitly approved.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Btn
                  variant="gold"
                  testId="nominate-another"
                  onClick={() => {
                    setDone(false);
                    setStep(0);
                    setBanner({ status: "idle", message: "" });
                  }}
                >
                  Submit Another Nomination
                </Btn>
                <Link
                  to="/emerald-honours"
                  className="inline-flex items-center gap-2 rounded-full border-2 border-white/30 px-5 py-3 text-sm font-bold text-white transition-colors hover:border-white hover:bg-white/10"
                >
                  Back to Emerald Honours
                </Link>
              </div>
            </div>
          ) : (
            <>
              {/* STEPPER */}
              <ol className="mb-10 grid grid-cols-4 gap-2" data-testid="nominate-stepper">
                {STEPS.map((label, i) => (
                  <li key={label} className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <span
                        data-testid={`nominate-step-${i}`}
                        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold transition-colors ${
                          i < step
                            ? "bg-bka-gold text-white"
                            : i === step
                              ? "bg-bka-emerald text-white ring-4 ring-bka-emerald/25"
                              : "bg-white/10 text-white/50"
                        }`}
                      >
                        {i < step ? "✓" : i + 1}
                      </span>
                      <span className="hidden h-[2px] flex-1 rounded bg-white/10 sm:block" />
                    </div>
                    <span
                      className={`text-[11px] font-bold leading-tight ${
                        i <= step ? "text-white" : "text-white/40"
                      }`}
                    >
                      {label}
                    </span>
                  </li>
                ))}
              </ol>

              <form onSubmit={submit} className="space-y-6" data-testid="nomination-form" noValidate>
                <Honeypot value={form.honeypot} onChange={set("honeypot")} />

                <div className="rounded-3xl border border-white/10 bg-white p-6 lg:p-8">
                  {step === 0 && (
                    <div className="space-y-5">
                      <Field label="Nominee Name" htmlFor="n-name" required error={errors.nominee_name}>
                        <TextInput
                          id="n-name"
                          data-testid="nomination-nominee-name"
                          value={form.nominee_name}
                          onChange={set("nominee_name")}
                          error={errors.nominee_name}
                          placeholder="Enter full name or organisation name"
                        />
                      </Field>
                      <Field
                        label="Category"
                        htmlFor="n-category"
                        required
                        error={errors.category}
                        hint="Categories are managed by the association."
                      >
                        <SelectInput
                          id="n-category"
                          data-testid="nomination-category"
                          value={form.category}
                          onChange={set("category")}
                          error={errors.category}
                        >
                          <option value="">Select a category</option>
                          {(config?.categories || []).map((c) => (
                            <option key={c.id} value={c.id}>{c.label}</option>
                          ))}
                        </SelectInput>
                      </Field>
                      <Field
                        label="Nominee Bio / Background"
                        htmlFor="n-bio"
                        hint="Their work, role, achievements or organisation."
                      >
                        <TextArea
                          id="n-bio"
                          data-testid="nomination-bio"
                          value={form.nominee_bio}
                          onChange={set("nominee_bio")}
                          rows={5}
                          placeholder="Tell us about the nominee…"
                        />
                      </Field>
                    </div>
                  )}

                  {step === 1 && (
                    <Field
                      label="Reason for Nomination"
                      htmlFor="n-reason"
                      required
                      error={errors.nomination_reason}
                      hint="Why should this person or organisation be recognised?"
                    >
                      <TextArea
                        id="n-reason"
                        data-testid="nomination-reason"
                        value={form.nomination_reason}
                        onChange={set("nomination_reason")}
                        error={errors.nomination_reason}
                        rows={9}
                        placeholder="Tell us how this individual or organisation has made an impact…"
                      />
                    </Field>
                  )}

                  {step === 2 && (
                    <div className="grid gap-5 sm:grid-cols-2">
                      <Field
                        label="Your Full Name"
                        htmlFor="n-your-name"
                        required
                        error={errors.nominator_name}
                        className="sm:col-span-2"
                      >
                        <TextInput
                          id="n-your-name"
                          data-testid="nomination-nominator-name"
                          value={form.nominator_name}
                          onChange={set("nominator_name")}
                          error={errors.nominator_name}
                          placeholder="Enter your full name"
                        />
                      </Field>
                      <Field label="Your Email" htmlFor="n-your-email" required error={errors.nominator_email}>
                        <TextInput
                          id="n-your-email"
                          type="email"
                          data-testid="nomination-nominator-email"
                          value={form.nominator_email}
                          onChange={set("nominator_email")}
                          error={errors.nominator_email}
                          placeholder="you@example.com"
                        />
                      </Field>
                      <Field label="Your Phone" htmlFor="n-your-phone">
                        <TextInput
                          id="n-your-phone"
                          data-testid="nomination-nominator-phone"
                          value={form.nominator_phone}
                          onChange={set("nominator_phone")}
                          placeholder="(242) 000-0000"
                        />
                      </Field>
                    </div>
                  )}

                  {step === 3 && (
                    <div className="space-y-5" data-testid="nomination-review">
                      <h2 className="font-display text-xl font-bold text-bka-navy">Review your nomination</h2>
                      <dl className="divide-y divide-slate-100 text-sm">
                        {[
                          ["Nominee", form.nominee_name],
                          [
                            "Category",
                            config?.categories?.find((c) => c.id === form.category)?.label || form.category,
                          ],
                          ["Nominee bio", form.nominee_bio || "—"],
                          ["Reason", form.nomination_reason],
                          ["Your name", form.nominator_name],
                          ["Your email", form.nominator_email],
                          ["Your phone", form.nominator_phone || "—"],
                        ].map(([label, value]) => (
                          <div key={label} className="grid gap-1 py-3 sm:grid-cols-[150px_1fr]">
                            <dt className="font-bold text-bka-ink/55">{label}</dt>
                            <dd className="whitespace-pre-wrap text-bka-ink">{value}</dd>
                          </div>
                        ))}
                      </dl>
                      <ConsentCheck
                        id="n-consent"
                        testId="nomination-consent"
                        checked={form.consent}
                        onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))}
                        error={errors.consent}
                      >
                        I confirm the information above is accurate to the best of my knowledge, and I
                        understand my details will be used by the Bahamas Kidney Association only to review
                        and follow up on this nomination.
                      </ConsentCheck>
                    </div>
                  )}

                  <div className="mt-8">
                    <FormBanner status={banner.status} message={banner.message} testId="nomination-banner" />
                  </div>

                  <div className="mt-6 flex flex-wrap items-center gap-3">
                    {step > 0 && (
                      <button
                        type="button"
                        data-testid="nomination-back"
                        onClick={() => setStep((s) => s - 1)}
                        className="rounded-full border-2 border-slate-200 px-5 py-3 text-sm font-bold text-bka-navy transition-colors hover:border-bka-emerald hover:text-bka-emerald"
                      >
                        Back
                      </button>
                    )}
                    {step < 3 ? (
                      <Btn testId="nomination-next" onClick={next}>
                        Continue
                      </Btn>
                    ) : (
                      <Btn testId="nomination-submit" type="submit" disabled={banner.status === "loading"}>
                        {banner.status === "loading" ? "Submitting…" : "Submit Nomination"}
                      </Btn>
                    )}
                  </div>
                </div>
              </form>
            </>
          )}
        </div>

        <aside className="lg:pt-24">
          <div className="rounded-3xl border border-white/10 bg-bka-emCard p-7">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-bka-emerald/20 text-bka-gold">
              <Gem className="h-5 w-5" />
            </span>
            <h2 className="mt-5 font-display text-xl font-bold leading-tight text-white">
              Together We Recognize Change
            </h2>
            <p className="mt-4 text-sm leading-[1.8] text-white/70">
              Your nomination helps us highlight the extraordinary people and organisations supporting
              kidney health in The Bahamas.
            </p>
            <ul className="mt-6 space-y-3 border-t border-white/10 pt-6 text-sm text-white/65">
              {[
                "Nominations are private and reviewed by the association.",
                "Nothing is published unless it is explicitly approved.",
                "We may contact you if we need more information.",
              ].map((t) => (
                <li key={t} className="flex gap-2.5">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-bka-emerald" />
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}
